import type { Doc } from "building-types/doc";
import type { ElevationHandle } from "building-types/handles/docEntityHandleTypes";
import type { Elevation } from "building-types/options/elevationOptionSet";
import type { ElevationOptionSet } from "building-types/options/optionSet";
import type { Table } from "common/data/table";
import { BuiltinHomeSettingsId } from "../settings/builtinHomeSettingsId";
import { docGetElevationOptionSet, docGetElevationOptionSetOrThrow } from "./docGetOptionSets";

function getElevation(elevationOptionSet: ElevationOptionSet, id: string): Elevation {
  return {
    id,
    name: elevationOptionSet.name,
    entityType: "ELEVATION",
    option: elevationOptionSet.options[id],
    baseSettings: elevationOptionSet.baseSettings[id] || BuiltinHomeSettingsId.Default,
    cameraPositions: elevationOptionSet.cameraPositions[id],
    images: elevationOptionSet.images[id],
  };
}

export function docGetElevation(doc: Pick<Doc, "optionSets">, id: string): Elevation | null {
  const elevationOptionSet = docGetElevationOptionSet(doc);
  if (elevationOptionSet) {
    return getElevation(elevationOptionSet, id);
  }
  return null;
}

export function docGetElevationOrThrow(doc: Pick<Doc, "optionSets">, id: string): Elevation {
  const elevationOptionSet = docGetElevationOptionSetOrThrow(doc);
  return getElevation(elevationOptionSet, id);
}

export function docGetElevations(doc: Pick<Doc, "optionSets">): Table<Elevation> {
  const elevationOptionSet = docGetElevationOptionSetOrThrow(doc);
  return Object.fromEntries(
    Object.keys(elevationOptionSet.options).map((id) => [id, getElevation(elevationOptionSet, id)]),
  );
}

export function docGetDefaultElevation(doc: Pick<Doc, "optionSets">): Elevation | null {
  const elevationOptionSet = docGetElevationOptionSet(doc);
  if (elevationOptionSet) {
    return getElevation(elevationOptionSet, elevationOptionSet.defaultValue);
  }
  return null;
}

export function docGetDefaultElevationOrThrow(doc: Pick<Doc, "optionSets">): Elevation {
  const elevationOptionSet = docGetElevationOptionSetOrThrow(doc);
  return getElevation(elevationOptionSet, elevationOptionSet.defaultValue);
}

export function docElevationIsDefault(doc: Pick<Doc, "optionSets">, handle: ElevationHandle): boolean {
  const elevationOptionSet = docGetElevationOptionSetOrThrow(doc);
  const elevation = docGetElevation(doc, handle.id);

  if (!elevation) {
    return false;
  }

  return elevationOptionSet.defaultValue === elevation.id;
}
